import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import { PrimaryButton } from "@/components/shared/ui";
import { DocumentItem } from "@/components/applicant/DocumentItem";
import { BankStage } from "@/components/applicant/BankStage";
import { ErrorBox, PassBox } from "@/components/applicant/stageUi";
import { TenantLoadError, TenantLoading, TenantShell } from "@/components/applicant/TenantShell";

/** The agent's Request-info link: opens ONLY the step that needs fixing. */
export default function FixStep() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/a/${token}`)).data);
      setError("");
    } catch (err) {
      setError(errorText(err));
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const checking = data?.checklist.some((i) => i.documents.some((d) => d.status === "checking"));
  useEffect(() => {
    if (!checking) return undefined;
    const t = setInterval(load, 1000);
    return () => clearInterval(t);
  }, [checking, load]);

  const send = async () => {
    setBusy(true);
    try {
      setData((await api.post(`/a/${token}/request/done`)).data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (!data) {
    body = error ? <TenantLoadError error={error} onRetry={load} testid="fix-load-error" /> : <TenantLoading />;
  } else if (!data.scope) {
    body = <ErrorBox testid="fix-invalid">This link isn't a fix link. Please use the link in the agent's email.</ErrorBox>;
  } else {
    const r = data.scope;
    const item = data.checklist.find((i) => i.key === r.step);
    body = (
      <div className="rise mx-auto max-w-xl md:pt-6" data-testid="fix-step">
        <p className="text-xs font-medium text-muted-foreground">{data.let.address}</p>
        <h1 className="mt-2 text-2xl font-semibold">Fix one step: {r.step_label}</h1>
        <div className="mt-4 rounded-xl border bg-background p-4 text-base" data-testid="fix-agent-message">
          <span className="font-medium">The agent says: </span>{r.message}
        </div>
        {r.resolved_at ? (
          <div className="mt-6" data-testid="fix-sent"><PassBox>Sent. The agent will take another look. You can close this page.</PassBox></div>
        ) : (
          <>
            <div className="mt-6">
              {!item ? <ErrorBox>This step is no longer on your checklist.</ErrorBox>
                : r.step === "bank_statements" ? <BankStage data={data} token={token} reload={load} single />
                : <DocumentItem item={item} token={token} onUpdated={load} compact />}
            </div>
            <div className="mt-6">
              <PrimaryButton size="lg" onClick={send} isLoading={busy} disabled={checking} testid="fix-send-button">Send to the agent</PrimaryButton>
              <p className="mt-2 text-center text-xs text-muted-foreground">Everything else you sent stays as it is.</p>
            </div>
          </>
        )}
        {error && <ErrorBox className="mt-4">{error}</ErrorBox>}
      </div>
    );
  }

  return <TenantShell agency={data?.let.agency_name}>{body}</TenantShell>;
}
